import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSave, faArrowLeft, faList, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createCaregiver, updateCaregiver, fetchCaregiver, setCurrentCaregiver } from '../../../../store/slices/caregiverSlice';
import { fetchPractice, addCaregiverOptimistic, fetchPracticePatients } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import './AddCaregiver.css';
import '../../PatientManagement/AddPatient/AddPatientModal.css';
import '../../../../components/Common/InactivationConfirmModal/InactivationConfirmModal.css';
import api from '../../../../services/api';
import { validatePassword, applyPasswordServerFieldError } from '../../../../utils/passwordValidation';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import ReassignmentModal from '../../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';

const AddCaregiver = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');
  const dispatch = useDispatch();
  const { currentCaregiver, isLoading, isUpdating, isCreating } = useSelector(state => state.caregiver);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const isEditMode = !!editId;
  const excludeUserId = isEditMode ? (editId ?? currentCaregiver?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    mobileAppAccess: 'Disabled',
    timeZone: '',
    status: 'Active'
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeCaregivers, setAlternativeCaregivers] = useState([]);
  const [reassignments, setReassignments] = useState({}); // { patientId: newCaregiverId }
  const [isReassigning, setIsReassigning] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);

  const getProviderSubmissionErrorMessage = (error, fallbackMessage) => {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallbackMessage
    );
  };


  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);

  const [errors, setErrors] = useState({});

  // Fetch caregiver data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      dispatch(setCurrentCaregiver(null));
      dispatch(fetchCaregiver({ practiceId, caregiverId: editId }));
    }
  }, [isEditMode, editId, practiceId, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    const practiceName = currentPractice?.practice_name || 'Practice';
    const fromDashboard = !!location.state?.fromDashboard;
    const rootLabel = fromDashboard ? 'Dashboard' : 'Practices';
    const rootPath = fromDashboard ? '/dashboard' : '/practices';
    const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';


    updateBreadcrumbs([
      { label: rootLabel, path: rootPath },
      { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
      { label: 'Caregivers', path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=caregiver${dashboardTabSuffix}` },

      { label: isEditMode ? 'Edit Caregiver' : 'Add Caregiver', path: null, active: true }
    ]);

  }, [practiceId, isEditMode, currentPractice, updateBreadcrumbs]);

  // Populate form when caregiver data is loaded
  useEffect(() => {
    if (isEditMode && currentCaregiver && String(currentCaregiver.id) === String(editId)) {
      const stateId = currentCaregiver.address?.state_id || currentCaregiver.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: (currentCaregiver.title_id !== null && currentCaregiver.title_id !== undefined)
          ? String(currentCaregiver.title_id)
          : (TITLES.find(t => t.label === currentCaregiver.title)?.id ? String(TITLES.find(t => t.label === currentCaregiver.title).id) : ''),
        firstName: currentCaregiver.first_name || '',
        middleInitial: currentCaregiver.middle_name || '',
        lastName: currentCaregiver.last_name || '',
        username: currentCaregiver.username || currentCaregiver.user?.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentCaregiver.address?.address_line_1 || currentCaregiver.address_line_1 || '',
        addressLine2: currentCaregiver.address?.address_line_2 || currentCaregiver.address_line_2 || '',
        city: currentCaregiver.address?.city || currentCaregiver.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: currentCaregiver.address?.zip_code || currentCaregiver.zip_code ? String(currentCaregiver.address?.zip_code || currentCaregiver.zip_code) : '',
        homePhone: formatPhoneNumber(currentCaregiver.cell_phone || ''),
        cellPhone: formatPhoneNumber(currentCaregiver.phone || ''),
        fax: formatPhoneNumber(currentCaregiver.fax || ''),
        email: currentCaregiver.email || '',
        twoWayAuth: (currentCaregiver.is_two_way_auth === 1 || currentCaregiver.is_two_way_auth === true || currentCaregiver.two_way_auth === 'Enabled' || currentCaregiver.two_way_authentication === 'Enabled') ? 'Enabled' : 'Disabled',
        mobileAppAccess: Number(currentCaregiver.mobile_app_access) === 1 ? 'Enabled' : 'Disabled',
        timeZone: normalizeTimeZone(currentCaregiver.time_zone) || '',
        status: currentCaregiver.status ? (currentCaregiver.status.charAt(0).toUpperCase() + currentCaregiver.status.slice(1).toLowerCase()) : 'Active'
      };
      setFormData(mappedData);
      setInitialFormData(mappedData);
      setLoadedCredentials({
        username: mappedData.username || '',
        email: mappedData.email || '',
      });
    }
  }, [isEditMode, currentCaregiver, editId]);

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
      if (value.length > 20) {
        return;
      }
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      // In Edit mode, if the value is empty, we don't change the password, so no error.
      // In Add mode, we don't show the "required" error in real-time until they click Add.
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        // Only show length/complexity errors if the user has typed something
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          // In Edit Mode, if it's just a length error and the user might be starting to type, 
          // or if it's an auto-fill, we might want to be careful.
          // But usually, real-time feedback is preferred. 
          // The user specifically wants it "not by default".
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // 4. City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 5. Status Change Detection
    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        setShowConfirmationModal(true);
        return;
      }
    }

    // State Sync — handled below with error clearing in setFormData

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));

      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };

    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // If state name changed, sync stateId
      if (name === 'state') {
        const selectedState = US_STATES.find(s => s.name === value);
        if (selectedState) {
          updated.stateId = selectedState.id;
        }
      }
      
      return updated;
    });

    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };


  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';

    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }

    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    
    // City alpha validation
    if (!formData.city?.trim()) {
      newErrors.city = 'City is required';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.city)) {
      newErrors.city = 'City can only contain alphabets';
    }

    if (!formData.state?.trim()) newErrors.state = 'State is required';
    
    // Zip Code
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    // Email validation (trim extra spaces)
    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    /* Hidden Fields Validation
    if (!formData.twoWayAuth?.trim()) newErrors.twoWayAuth = 'Two Way Authentication is required';
    if (!formData.timeZone?.trim()) newErrors.timeZone = 'Time Zone is required';
    */
    if (!formData.status?.trim()) newErrors.status = 'Status is required';

    // Password confirmation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }


    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    if (validateForm()) {
      const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
      const emailOk = await ensureEmailAvailable(formData.email, setErrors);
      if (!usernameOk || !emailOk) {
        setIsSubmitting(false);
        toast.warn(VALIDATION_SUMMARY_MESSAGE);
        return;
      }
      try {
        await saveCaregiver();
      } catch (error) {
        console.error('Submit catch:', error);
        if (applyPasswordServerFieldError(setErrors, error)) {
          return;
        }
        const errorMessage = getProviderSubmissionErrorMessage(error, '');
        if (errorMessage.toLowerCase().includes('missing') || errorMessage.toLowerCase().includes('required')) {
          toast.warn(VALIDATION_SUMMARY_MESSAGE);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
    }
  };


  const saveCaregiver = async () => {
    // Transform form data to match backend API
    const caregiverData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      middle_name: formData.middleInitial,
      email: (formData.email || '').trim(),
      phone: (formData.cellPhone || '').replace(/\D/g, ''),
      cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
      fax: formData.fax,
      address_line_1: formData.addressLine1,
      address_line_2: formData.addressLine2,
      city: formData.city,
      state_id: formData.stateId || 1,
      zip_code: formData.zipCode,
      username: formData.username.trim(),
      password: formData.password?.trim() || undefined,
      status: formData.status || 'Active',
      is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
      mobile_app_access: formData.mobileAppAccess === 'Enabled' ? 1 : 0,
      time_zone: formData.timeZone || 'EST - Eastern Time',
      title_id: formData.title ? parseInt(formData.title) : null
    };

    try {
      if (isEditMode) {
        if (!caregiverData.password) delete caregiverData.password;
        await dispatch(updateCaregiver({ practiceId, caregiverId: editId, caregiverData })).unwrap();
        toast.success('Caregiver updated successfully');
      } else {
        const tempId = `temp-caregiver-${Date.now()}`;
        const optimisticCaregiver = {
          id: tempId,
          name: [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() || 'N/A',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: (formData.email || '').trim(),
          phone: formData.cellPhone || 'N/A',
          home_phone: formData.cellPhone || 'N/A',
          cell_phone: formData.homePhone || 'N/A',
          fax: formData.fax || 'N/A',
          status: formData.status || 'Active',
          is_two_way_auth: caregiverData.is_two_way_auth,
          two_way_authentication_enabled: caregiverData.is_two_way_auth === 1,
          address_line_1: formData.addressLine1 || 'N/A',
          address: {
            address_line_1: formData.addressLine1 || 'N/A'
          }
        };

        dispatch(addCaregiverOptimistic({ practiceId, caregiver: optimisticCaregiver }));
        await dispatch(createCaregiver({ practiceId, caregiverData, tempId })).unwrap();
        toast.success('Caregiver added successfully');
      }

      const fromDashboard = location.state?.fromDashboard;
      const path = fromDashboard ? `/dashboard/${practiceId}?tab=caregiver` : `/practices/${practiceId}?tab=caregiver`;
      navigate(path, { state: { fromDashboard } });
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    }
  };


  const startInactivationFlow = async () => {
    setShowConfirmationModal(false);
    setIsSubmitting(true);
    try {
      // 1. Fetch patients for this caregiver
      const patientsResponse = await api.get(`/practices/${practiceId}/patients`, { 
        params: { caregiverId: editId, limit: 100 } 
      });
      const patients = patientsResponse.data?.data?.patients || [];
      setAssignedPatients(patients);

      // 2. Fetch alternative caregivers
      const caregiversResponse = await api.get(`/practices/${practiceId}/caregivers`, { params: { limit: 1000 } });
      const rawCaregivers =
        caregiversResponse.data?.data?.caregivers ||
        caregiversResponse.data?.caregivers ||
        caregiversResponse.data?.data ||
        caregiversResponse.data ||
        [];
      const caregivers = (Array.isArray(rawCaregivers) ? rawCaregivers : []).filter((c) => {
        const status = c?.status;
        const isActive =
          status === 1 ||
          status === true ||
          (typeof status === 'string' && status.toLowerCase() === 'active');
        return String(c?.id) !== String(editId) && isActive;
      });
      setAlternativeCaregivers(caregivers);

      if (patients.length > 0) {
        const initialReassignments = {};
        patients.forEach((p) => {
          initialReassignments[p.id] = '';
        });
        setReassignments(initialReassignments);
        setShowReassignmentModal(true);
      } else {
        setFormData(prev => ({ ...prev, status: 'Inactive' }));
        toast.info('No patients assigned. You can now save the caregiver as Inactive.');
      }
    } catch (err) {
      console.error('Inactivation flow failed:', err);
      toast.error('Failed to initiate reassignment workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    setIsReassigning(true);
    try {
      const missingAssignments = assignedPatients.some(patient => !reassignments[patient.id]);
      if (missingAssignments) {
        toast.warning('Please select a new caregiver for all patients');
        setIsReassigning(false);
        return;
      }

      const reassignArray = Object.entries(reassignments).map(([pid, cid]) => ({
        patientId: pid,
        caregiverId: cid
      }));

      await api.post(`/practices/${practiceId}/patients/batch-reassign`, { reassignments: reassignArray });
      
      setShowReassignmentModal(false);
      setFormData(prev => ({ ...prev, status: 'Inactive' }));
      dispatch(fetchPracticePatients({ id: practiceId, params: { page: 1, limit: 1000 } }));
      toast.success('Patients reassigned. Status updated to Inactive.');
    } catch (err) {
      console.error('Batch reassignment failed:', err);
      toast.error('Failed to reassign patients');
    } finally {
      setIsReassigning(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCancel = () => {
    const fromDashboard = location.state?.fromDashboard;
    const path = fromDashboard ? `/dashboard/${practiceId}?tab=caregiver` : `/practices/${practiceId}?tab=caregiver`;
    navigate(path, { state: { fromDashboard } });
  };


  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  if (isEditMode && isLoading) {
    return (
      <div className="ac-container">
        <div className="add-patient-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="ac-container">
      <AddFormModel
        title={isEditMode ? 'Edit Caregiver' : 'Add Caregiver'}
        currentPractice={currentPractice}
        getLogoUrl={getLogoUrl}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        leftSection={
          <div className="ac-form-redesign-wrapper">
            <div className="pif-demo-section-label">Personal Information</div>

            {/* Title | First Name */}
            <div className="ac-row ac-row-title-first">
              <div className="ac-field w-title">
                <label htmlFor="title">Title</label>
                <select id="title" name="title" value={formData.title} onChange={handleInputChange}>
                  <option value="">- Select -</option>
                  {TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="ac-field w-first-name">
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
                <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
            </div>

            {/* M I | Last Name */}
            <div className="ac-row ac-row-mi-last">
              <div className="ac-field w-mi">
                <label htmlFor="middleInitial">Middle Name</label>
                <input type="text" id="middleInitial" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} maxLength={20} placeholder="e.g., Marie" />
              </div>
              <div className="ac-field w-last-name">
                <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>

            {/* Home Phone | Cell Phone | Fax */}
            <div className="ac-row ac-row-3col">
              <div className="ac-field">
                <label htmlFor="homePhone">Home Phone</label>
                <PhoneNumberInput id="homePhone" name="homePhone" value={formData.homePhone} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.homePhone && <span className="error-text">{errors.homePhone}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="cellPhone">Cell Phone <span className="required">*</span></label>
                <PhoneNumberInput id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} placeholder="123-231-2345" required />
                {errors.cellPhone && <span className="error-text">{errors.cellPhone}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="fax">Fax</label>
                <PhoneNumberInput id="fax" name="fax" value={formData.fax} onChange={handleInputChange} placeholder="123-231-2345" />
                {errors.fax && <span className="error-text">{errors.fax}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

            <div className="ac-row ac-row-2col">
              <div className="ac-field">
                <label htmlFor="username">Username <span className="required">*</span></label>
                <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} autoComplete="username" placeholder="e.g., john.doe" maxLength={USERNAME_MAX_LENGTH} />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., caregiver@example.com" required />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            {/* Password | Confirm Password */}
            <div className="ac-row ac-row-2col">
              <div className="ac-field">
                <label htmlFor="password">Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ac-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ac-password-toggle" onClick={togglePasswordVisibility} aria-label="Toggle password visibility">
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="ac-password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? 'Leave blank to keep current' : 'Enter at least 8 characters'}
                  />
                  <button type="button" className="ac-password-toggle" onClick={toggleConfirmPasswordVisibility} aria-label="Toggle confirm password visibility">
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

            {/* Address Line 1 */}
            <div className="ac-field full-width">
              <label htmlFor="addressLine1">Address Line 1 <span className="required">*</span></label>
              <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
              {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
            </div>

            {/* Address Line 2 */}
            <div className="ac-field full-width">
              <label htmlFor="addressLine2">Address Line 2</label>
              <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
            </div>

            {/* City | State | Zip Code */}
            <div className="ac-row ac-row-3col">
              <div className="ac-field">
                <label htmlFor="city">City <span className="required">*</span></label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="state">State <span className="required">*</span></label>
                <select id="state" name="state" value={formData.state} onChange={handleInputChange} required>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
                {errors.state && <span className="error-text">{errors.state}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="zipCode">Zip Code <span className="required">*</span></label>
                <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
              </div>
            </div>

            <div className="ac-field full-width">
              <label htmlFor="timeZone">Time Zone <span className="required">*</span></label>
              <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange} required>
                <option value="">Select Time Zone</option>
                {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              {errors.timeZone && <span className="error-text">{errors.timeZone}</span>}
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

            <div className="ac-row ac-row-2col">
              <div className="ac-field">
                <label htmlFor="status">Status <span className="required">*</span></label>
                <select id="status" name="status" value={formData.status || 'Active'} onChange={handleInputChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status && <span className="error-text">{errors.status}</span>}
              </div>
              <div className="ac-field">
                <label htmlFor="twoWayAuth">Two Way Authentication <span className="required">*</span></label>
                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange}>
                  <option value="Enabled">Enabled</option>
                  <option value="Disabled">Disabled</option>
                </select>
                {errors.twoWayAuth && <span className="error-text">{errors.twoWayAuth}</span>}
              </div>
            </div>

            <div className="ac-row ac-row-2col">
              <div className="ac-field">
                <label htmlFor="mobileAppAccess">Mobile App Access</label>
                <select id="mobileAppAccess" name="mobileAppAccess" value={formData.mobileAppAccess || 'Disabled'} onChange={handleInputChange}>
                  <option value="Disabled">Off</option>
                  <option value="Enabled">On</option>
                </select>
              </div>
            </div>
          </div>
        }

        rightSection={null}
        footerActions={
          <>
            {isEditMode ? (
              <button
                type="button"
                className="btn-submit"
                onClick={() => navigate(`/event-logs/Caregiver/${editId}?practiceId=${practiceId || ''}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px', backgroundColor: 'var(--ev-navy-dark)', marginRight: 'auto', height: '40px' }}
              >
                <FontAwesomeIcon icon={faList} className="me-2" /> Event Logs
              </button>
            ) : (
              <div style={{ marginRight: 'auto' }}></div>
            )}
            <div className="add-form-actions-wrap">
              <FormActionButtons
                onCancel={handleCancel}
                disabled={isSubmitting || isLoading || isUpdating || isCreating || (isEditMode && !isChanged)}
                isSubmitting={isSubmitting || isUpdating || isCreating}
                isEditMode={isEditMode}
                addLabel="Save Caregivers"
                updateLabel="Update Caregivers"
              />
            </div>
          </>
        }
      />

      {/* Validation Warning Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-caregiver-validation-modal"
      >
        <Modal.Body className="add-caregiver-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-caregiver-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="add-caregiver-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
          </div>
          <h4 className="add-caregiver-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button 
            type="button"
            className="add-caregiver-modal-btn" 
            onClick={() => setShowValidationModal(false)}
            style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

      {/* Inactivation Confirmation Modal */}
      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        centered
        dialogClassName="add-caregiver-confirm-dialog"
        contentClassName="add-caregiver-confirm-content"
      >
        <Modal.Body className="add-caregiver-confirm-body text-center">
          {/* <div className="add-caregiver-confirm-icon-wrapper mx-auto">
            <FontAwesomeIcon icon={faInfoCircle} />
          </div> */}
          <h4 className="add-caregiver-confirm-title">Are you sure?</h4>
          <p className="add-caregiver-confirm-text">
            Remove access to this caregiver and reassign assigned patients.
          </p>

          <div className="add-caregiver-confirm-actions">
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--ghost"
              onClick={() => setShowConfirmationModal(false)}
            >
              No
            </button>
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--primary"
              onClick={startInactivationFlow}
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => {
          setShowReassignmentModal(false);
          setFormData((prev) => ({ ...prev, status: 'Active' }));
        }}
        title="Reassign Patients"
        description="Select a new caregiver for each patient before inactivation."
        patients={assignedPatients}
        alternatives={alternativeCaregivers}
        reassignments={reassignments}
        showMeta={false}
        showIndex={true}
        hideCurrentColumn={true}
        showOptionPlaceholder={true}
        onReassignmentChange={(patientId, caregiverId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: caregiverId }))
        }
        onAssignAll={(caregiverId) =>
          setReassignments((prev) => {
            const next = { ...prev };
            assignedPatients.forEach((patient) => {
              next[patient.id] = caregiverId;
            });
            return next;
          })
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select caregiver..."
        reassignToLabel="Reassign to caregiver"
        getCurrentAssignee={(patient) =>
          patient.caregiver_name ||
          patient.provider_name ||
          patient.assigned_to_name ||
          patient.current_caregiver_name ||
          ((patient.caregiver_first_name || patient.caregiver_last_name)
            ? `${patient.caregiver_first_name || ''} ${patient.caregiver_last_name || ''}`.trim()
            : '') ||
          ((patient.provider_first_name || patient.provider_last_name)
            ? `${patient.provider_first_name || ''} ${patient.provider_last_name || ''}`.trim()
            : '') ||
          'N/A'
        }
      />
    </div>
  );
};

export default AddCaregiver;